import type { SyntheticEvent } from 'react';
import { Box, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { Workout } from "../../api/workout-service";

import { QUIZ_BLUE, QUIZ_BORDER, QUIZ_INK, QUIZ_MUTED, QUIZ_RIGHT } from "./quiz-theme";

/** Lives a verification session starts with; other workouts have unlimited lives and show no hearts. */
const MAX_LIVES = 3;
const FALLBACK_COVER = '/assets/images/covers/cs-general.svg';
const HEART = 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z';

export type WorkoutTopBarProps = {
    workout: Workout;
    questionIndex: number;
};

/** The strip above the question: a way out, the workout, how each question went so far and the lives left. */
export function WorkoutTopBar({ workout, questionIndex }: WorkoutTopBarProps) {
    const total = workout.questions.length;
    const lives = workout.currentUser?.lives;
    const topic = workout.questions[questionIndex]?.topicTitle;

    return (
        <Stack component="header" direction="row" alignItems="center" spacing={ { xs: 1.5, sm: 2.5 } } sx={ {
            minHeight: 72, flexShrink: 0, bgcolor: '#FFFFFF', borderBottom: `1px solid ${ QUIZ_BORDER }`, px: { xs: 2, sm: 4 }, color: QUIZ_INK,
        } }>
            <Box component={ RouterLink } to="/workouts" aria-label="Leave workout" sx={ {
                width: 44, height: 44, boxSizing: 'border-box', borderRadius: '10px', border: `1px solid ${ QUIZ_BORDER }`, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: QUIZ_MUTED,
                '&:hover': { borderColor: '#CFD6DE', color: QUIZ_INK },
            } }>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18"/>
                </svg>
            </Box>

            <Box component="img" src={ `/assets/images/covers/${ workout.topics[0]?.id ?? 'cs-general' }.svg` } alt="" onError={ handleCoverError }
                 sx={ { display: { xs: 'none', sm: 'block' }, width: 48, height: 36, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 } }/>

            <Stack sx={ { minWidth: 0 } }>
                <Box component="span" sx={ { fontSize: 15, fontWeight: 800 } }>{ workout.workoutTitle }</Box>
                <Box component="span" sx={ { fontSize: 12, color: QUIZ_MUTED } }>
                    Question { questionIndex + 1 } of { total }{ topic && topic !== workout.workoutTitle ? ` · ${ topic }` : '' }
                </Box>
            </Stack>

            <Stack direction="row" spacing={ 0.75 } aria-label={ `Progress: question ${ questionIndex + 1 } of ${ total }` } role="img"
                   sx={ { flexGrow: 1, maxWidth: 420, mx: 'auto !important', display: { xs: 'none', md: 'flex' } } }>
                { workout.questions.map((question, index) => (
                    <Box key={ question.id } sx={ { flex: 1, height: 8, borderRadius: '4px', bgcolor: segmentColor(workout, index, questionIndex) } }/>
                )) }
            </Stack>

            { lives != null && (
                <Stack direction="row" spacing={ 0.5 } aria-label={ `${ lives } of ${ MAX_LIVES } lives left` } role="img" sx={ { ml: 'auto !important' } }>
                    { Array.from({ length: Math.max(MAX_LIVES, lives) }, (_, index) => (
                        <svg key={ index } width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                            { index < lives
                                ? <path d={ HEART } fill="#E5484D"/>
                                : <path d={ HEART } fill="none" stroke="#B5BEC8" strokeWidth="2"/> }
                        </svg>
                    )) }
                </Stack>
            ) }
        </Stack>
    );
}

/** Green for a right answer, red for a wrong or skipped one, blue for the question on screen, grey ahead. */
function segmentColor(workout: Workout, index: number, current: number): string {
    const question = workout.questions[index];
    const mine = question.answers.find((answer) => answer.user === workout.currentUser?.id);

    if (mine) return mine.index === question.correct ? QUIZ_RIGHT : '#D93A3A';
    if (index === current) return QUIZ_BLUE;

    return '#DDE3EA';
}

/** A topic without a cover of its own falls back to the neutral one. */
function handleCoverError(event: SyntheticEvent<HTMLImageElement>) {
    const image = event.currentTarget;
    if (!image.src.endsWith(FALLBACK_COVER)) image.src = FALLBACK_COVER;
}
